import { initiateNangoAuth, redirectToOAuth } from 'v2/pages/settings/integrations/nango'
import { useReconnectOrgAppConnectionMutation } from 'v2/pages/settings/integrations/queries'
import { useIntegrationRollout } from 'v2/shared/hooks/use-integration-rollout'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import { logger } from 'v2/utils/logger'
import { errorToast } from 'v2/utils/toasts'
import { requiresAciReconnect } from '../../utils/integration-availability'
import { getSafeIntegrationErrorMetadata } from '../../utils/safe-error-metadata'

type ReconnectAppCallback = () => void
type ReconnectAppErrorCallback = (error: Error) => void

interface UseReconnectAppReturn {
  reconnectApp: (
    app: RExternalOrgApp,
    onSuccess?: ReconnectAppCallback,
    onError?: ReconnectAppErrorCallback,
  ) => void
  isReconnecting: boolean
}

export const useReconnectApp = (): UseReconnectAppReturn => {
  const { useAciIntegrations } = useIntegrationRollout()
  const { mutate, isPending } = useReconnectOrgAppConnectionMutation()

  const reconnectApp = (
    app: RExternalOrgApp,
    onSuccess?: ReconnectAppCallback,
    onError?: ReconnectAppErrorCallback,
  ) => {
    if (requiresAciReconnect(app, useAciIntegrations)) {
      errorToast({
        title: 'Reconnect required',
        description:
          'Use Connect to set this integration up on the new integrations service before using it again.',
      })
      onError?.(new Error('Legacy connection cannot execute on the new integrations service'))
      return
    }

    mutate(
      { appId: app.id, source: app.integration_source },
      {
        onSuccess: async (data) => {
          // ACI OAuth path: backend returned a redirect URL -> navigate the
          // browser to the provider consent screen.
          if (data.oauth_redirect_url) {
            onSuccess?.()
            redirectToOAuth(data.oauth_redirect_url)
            return
          }

          // Legacy nango path: backend returned a connect session token.
          if (data.connect_session_token && data.org_app) {
            try {
              await initiateNangoAuth({
                provider: app.provider,
                connection: data.org_app.connection_details.connection,
                credentials: data.org_app.connection_details.credentials,
                sessionToken: data.connect_session_token,
                appName: app.name,
              })
              onSuccess?.()
            } catch (error: unknown) {
              logger.error(
                'Integration reconnect authentication failed',
                getSafeIntegrationErrorMetadata(error),
              )
              onError?.(error instanceof Error ? error : new Error(String(error)))
            }
            return
          }

          // Neither path available -> surface an error.
          logger.error('Reconnect returned no oauth_redirect_url or connect_session_token')
          onError?.(new Error('No redirect URL'))
        },
        onError: (error) => {
          logger.error(
            'Integration reconnect request failed',
            getSafeIntegrationErrorMetadata(error),
          )
          errorToast({
            title: 'Reconnect Failed',
            description: 'Could not initiate reconnection.',
          })
          onError?.(error)
        },
      },
    )
  }

  return {
    reconnectApp,
    isReconnecting: isPending,
  }
}
